import {Component, Input, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {NzModalRef} from 'ng-zorro-antd/modal';
import {MfaService} from '../../../pages/login/service/mfa.service';
import {AuthService} from '../../../pages/login/service/auth.service';
import {Router} from '@angular/router';

/**
 * Modal hướng dẫn + hiển thị QR cho user đăng nhập LẦN ĐẦU
 * sau khi Admin bật 2FA hộ (adminSetupMfa).
 *
 * Luồng:
 *  1. BE trả messageCode=MFA_FIRST_LOGIN với data={ firstLoginToken, qrCodeUrl, secretKey, message }
 *  2. LoginComponent mở modal này, truyền vào 3 Input
 *  3. User quét QR → nhập OTP → component gọi verifyMfaFirstLogin()
 *  4. Thành công → đóng modal, navigate vào app
 */
@Component({
  selector: 'app-mfa-first-login',
  templateUrl: './mfa-first-login.component.html',
  styleUrls: ['./mfa-first-login.component.scss']
})
export class MfaFirstLoginComponent implements OnInit {

  /** Token tạm thời từ BE (TTL 10 phút) */
  @Input() firstLoginToken: string;

  /** URL dạng otpauth://totp/... – dùng để render QR */
  @Input() qrCodeUrl: string;

  /** Secret key dạng base32 – hiển thị để nhập tay nếu không quét được QR */
  @Input() secretKey: string;

  otpForm: FormGroup;
  isLoading = false;
  errorMessage: string = null;

  /** Bước hiện tại: 'qr' = đang xem QR, 'otp' = đang nhập OTP xác nhận */
  step: 'qr' | 'otp' = 'qr';

  /** Toggle hiển thị secret key */
  showSecret = false;

  constructor(
    private fb: FormBuilder,
    private modalRef: NzModalRef,
    private mfaService: MfaService,
    private authService: AuthService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.otpForm = this.fb.group({
      otp: [null, [Validators.required, Validators.minLength(6), Validators.maxLength(6), Validators.pattern('^[0-9]{6}$')]]
    });
  }

  /** Sang bước nhập OTP */
  goToOtp(): void {
    this.step = 'otp';
  }

  /** Quay lại xem QR */
  backToQr(): void {
    this.step = 'qr';
    this.errorMessage = null;
    this.otpForm.reset();
  }

  confirm(): void {
    if (this.otpForm.invalid) {
      Object.values(this.otpForm.controls).forEach(c => {
        c.markAsDirty();
        c.updateValueAndValidity({onlySelf: true});
      });
      return;
    }

    this.isLoading = true;
    this.errorMessage = null;
    const otp = this.otpForm.get('otp').value;

    this.mfaService.verifyMfaFirstLogin(this.firstLoginToken, otp).subscribe(
      res => {
        this.isLoading = false;
        if (res && res.access_token) {
          this.authService.saveStorage(res);
          this.modalRef.close({success: true});
          this.router.navigate(['']);
        } else {
          this.errorMessage = res?.message ?? 'Xác thực thất bại. Vui lòng thử lại.';
        }
      },
      error => {
        this.isLoading = false;
        switch (error?.messageCode) {
          case 'MFA_INVALID_OTP':
            this.errorMessage = 'Mã OTP không đúng. Hãy kiểm tra lại trong Google Authenticator.';
            break;
          case 'MFA_TOKEN_EXPIRED':
            this.errorMessage = 'Phiên thiết lập đã hết hạn (10 phút). Vui lòng đăng nhập lại.';
            this.modalRef.close({expired: true});
            break;
          default:
            this.errorMessage = error?.message ?? 'Có lỗi xảy ra. Vui lòng thử lại.';
        }
      }
    );
  }

  cancel(): void {
    this.modalRef.close(null);
  }

  /** Chỉ cho phép nhập số */
  onOtpKeyPress(event: KeyboardEvent): boolean {
    return /[0-9]/.test(event.key);
  }

  /** Copy secret key vào clipboard */
  copySecret(): void {
    if (this.secretKey) {
      navigator.clipboard.writeText(this.secretKey).catch(() => {});
    }
  }
}
